/**
 * Breadcrumb Component
 *
 * Hierarchical navigation breadcrumbs with responsive mobile design.
 * Desktop: Full trail with separators
 * Mobile: "< Back" button to parent
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { BreadcrumbProps } from './breadcrumb.types';
import { BREADCRUMB_STYLES, BREADCRUMB_SEPARATOR } from './breadcrumb.definitions';

export default function Breadcrumb({ items, className }: BreadcrumbProps) {
  const navigate = useNavigate();

  // If only one item (root), don't show breadcrumbs
  if (items.length <= 1) {
    return null;
  }

  // Get parent path for mobile back button (second-to-last item)
  const parentItem = items.length > 1 ? items[items.length - 2] : null;
  const parentPath = parentItem?.path || '/okr';

  const handleBackClick = () => {
    navigate(parentPath);
  };

  return (
    <div className={clsx('breadcrumb-container', className)}>
      {/* Mobile: Back Button */}
      <div className={BREADCRUMB_STYLES.mobileContainer}>
        <button
          onClick={handleBackClick}
          className={BREADCRUMB_STYLES.backButton}
          aria-label="Voltar"
        >
          <ChevronLeftIcon className={BREADCRUMB_STYLES.backIcon} />
          <span>Voltar</span>
        </button>
      </div>

      {/* Desktop: Full Breadcrumb Trail */}
      <nav
        className={BREADCRUMB_STYLES.desktopContainer}
        aria-label="Breadcrumb"
      >
        <ol className={BREADCRUMB_STYLES.list}>
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isClickable = !isLast && item.path;

            return (
              <li
                key={`${item.path || 'current'}-${index}`}
                className={BREADCRUMB_STYLES.item}
              >
                {/* Item */}
                {isClickable ? (
                  <Link
                    to={item.path!}
                    className={BREADCRUMB_STYLES.link}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className={BREADCRUMB_STYLES.currentPage}>
                    {item.label}
                  </span>
                )}

                {/* Separator (not after last item) */}
                {!isLast && (
                  <span
                    className={BREADCRUMB_STYLES.separator}
                    aria-hidden="true"
                  >
                    {BREADCRUMB_SEPARATOR}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
}
